import { BaseApiService } from "./baseApi";

interface UserParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  status: string;
  isVerified: boolean;
  isSuspended: boolean;
  createdAt: string;
  lastLogin?: string;
}

export class UsersApiService extends BaseApiService {
  async getUsers(params: UserParams = {}): Promise<{
    users: User[];
    total: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.status) queryParams.append("status", params.status);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const endpoint = `/users${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    return this.get(endpoint);
  }

  async getUserById(id: string): Promise<User> {
    return this.get<User>(`/users/${id}`);
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    return this.put<User>(`/users/${id}`, userData);
  }

  async deleteUser(id: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/users/${id}/admin-delete`);
  }

  async suspendUser(id: string): Promise<{
    success: boolean;
    user: { _id: string; isSuspended: boolean };
  }> {
    return this.patch(`/users/${id}/admin-suspend`, { suspended: true });
  }

  async unsuspendUser(id: string): Promise<{
    success: boolean;
    user: { _id: string; isSuspended: boolean };
  }> {
    return this.patch(`/users/${id}/admin-suspend`, { suspended: false });
  }

  async adminUpdateUser(
    id: string,
    data: Partial<{
      firstName: string;
      lastName: string;
      username: string;
      phoneNumber: string;
      bio: string;
      gender: string;
    }>
  ): Promise<{ success: boolean; user: Record<string, unknown> }> {
    return this.patch(`/users/${id}/admin-update`, data);
  }

  async updateUserFeatureFlags(
    id: string,
    featureFlags: Record<string, boolean>
  ): Promise<{
    success: boolean;
    user: { _id: string; email: string; featureFlags: Record<string, boolean> };
  }> {
    return this.patch(`/users/${id}/feature-flags`, { featureFlags });
  }

  async getUserStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    newUsersToday: number;
    newUsersThisWeek: number;
    newUsersThisMonth: number;
  }> {
    return this.get("/users/stats");
  }

  async getUserTracking(id: string): Promise<any> {
    return this.get(`/users/${id}/tracking`);
  }

  async getOnlineUsersCount(): Promise<{ count: number }> {
    return this.get("/users/online-count");
  }

  async getLiveUsers(): Promise<any[]> {
    return this.get("/users/live");
  }

  async getMessagesCount(): Promise<{ count: number }> {
    return this.get("/messages/count");
  }

  async getMessages(
    page: number,
    limit: number,
    search: string,
    chatId?: string
  ): Promise<any> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (search) queryParams.append("search", search);
    if (chatId) queryParams.append("chatId", chatId);

    const endpoint = `/messages?${queryParams.toString()}`;
    return this.get(endpoint);
  }

  async getMessageById(messageId: string): Promise<any> {
    return this.get(`/messages/${messageId}`);
  }

  async getSupportChats(
    page: number,
    limit: number,
    status: string,
    search?: string
  ): Promise<any> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    queryParams.append("status", status);
    if (search) queryParams.append("search", search);

    const endpoint = `/support-chats?${queryParams.toString()}`;
    return this.get(endpoint);
  }

  async getSupportChatById(chatId: string): Promise<any> {
    return this.get(`/support-chats/${chatId}`);
  }

  async sendSupportChatReply(chatId: string, message: string): Promise<any> {
    return this.post(`/support-chats/${chatId}/reply`, { message });
  }

  async sendSupportChatEmailReply(
    chatId: string,
    message: string
  ): Promise<any> {
    return this.post(`/support-chats/${chatId}/email-reply`, { message });
  }

  async updateSupportChatStatus(chatId: string, status: string): Promise<any> {
    return this.put(`/support-chats/${chatId}/status`, { status });
  }

  async getSupportChatsStats(): Promise<any> {
    return this.get("/support-chats/stats");
  }

  async getAppVersionStats(): Promise<any> {
    return this.get("/app-version/stats");
  }
}
